import { useCallback, useEffect, useState } from "react";
import { portalFetch } from "../api/client";

const PAGE_SIZE = 50;

function pageUrl(baseUrl: string, offset: number) {
	return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}limit=${PAGE_SIZE}&offset=${offset}`;
}

export function usePagedPortalItems<T extends { id: string }>(baseUrl: string) {
	const [items, setItems] = useState<T[]>([]);
	const [nextOffset, setNextOffset] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [failed, setFailed] = useState(false);

	const load = useCallback(
		async (offset = 0) => {
			offset === 0 ? setLoading(true) : setLoadingMore(true);
			try {
				const page = await portalFetch<{ items: T[] }>(pageUrl(baseUrl, offset));
				setItems((current) => {
					if (offset === 0) return page.items;
					const byId = new Map(current.map((item) => [item.id, item]));
					for (const item of page.items) byId.set(item.id, item);
					return [...byId.values()];
				});
				setNextOffset(offset + page.items.length);
				setHasMore(page.items.length === PAGE_SIZE);
				setFailed(false);
			} catch {
				setFailed(true);
				throw new Error("Portal page could not be loaded");
			} finally {
				setLoading(false);
				setLoadingMore(false);
			}
		},
		[baseUrl],
	);

	useEffect(() => {
		void load(0).catch(() => undefined);
	}, [load]);
	const refresh = useCallback(() => load(0), [load]);

	return {
		items,
		setItems,
		loading,
		loadingMore,
		failed,
		hasMore,
		refresh,
		loadMore: () => load(nextOffset),
	};
}
